import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Gauge, CheckCircle, WarningCircle, ArrowSquareOut, Printer, Lock, Handshake,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { LocationPicker, EmployeePicker } from '@/components/registration';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/enterprise/StatusBadge';
import { devApi } from './devApi';

const TARGET_PATH = {
  eng_equipment: '/engineering/equipment',
  mfg_equipment: '/manufacturing/equipment',
  mfg_item: '/manufacturing/items',
};

const LeftoverPartsSection = ({ parts, value, onChange, testPrefix = 'dev-leftover' }) => {
  const all = (parts || []);
  if (all.length === 0) return null;
  const ids = value.part_ids ?? all.map((p) => p.id);
  const toggle = (id) => {
    const next = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
    onChange({ part_ids: next });
  };
  return (
    <div className="rounded-lg border border-zinc-300 bg-zinc-50/70 px-3 py-2.5"
      data-testid={`${testPrefix}-section`}>
      <p className="text-xs font-bold text-zinc-800">
        余った部品の行き先（{all.length} 件）
      </p>
      <p className="mt-0.5 text-[11px] text-zinc-600">
        現物の置き場所を決めます。移動しても番号は変わりません（写しは作りません）。
        既定は「技術に残す」で、何も動きません。
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {[{ v: 'keep', l: '技術に残す（既定）' }, { v: 'move', l: '納入先へ移動' }].map((o) => (
          <button key={o.v} type="button" onClick={() => onChange({ action: o.v })}
            data-testid={`${testPrefix}-action-${o.v}`}
            className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${
              (value.action || 'keep') === o.v
                ? 'border-teal-600 bg-teal-600 text-white'
                : 'border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-100'}`}>
            {o.l}
          </button>
        ))}
      </div>
      {(value.action || 'keep') === 'move' && (
        <div className="mt-2 space-y-2">
          <div>
            <Label className="text-xs font-semibold text-zinc-700">移動先の場所 ＊</Label>
            <LocationPicker value={value.location_id || ''}
              onChange={(id) => onChange({ location_id: id })}
              testId={`${testPrefix}-location`} />
          </div>
          <ul className="divide-y divide-zinc-200 rounded-md border border-zinc-200 bg-white"
            data-testid={`${testPrefix}-list`}>
            {all.map((p) => (
              <li key={p.id} className="flex items-center gap-2 px-2.5 py-1.5 text-[11px]">
                <input type="checkbox" checked={ids.includes(p.id)} onChange={() => toggle(p.id)}
                  data-testid={`${testPrefix}-part-${p.id}`} />
                <span className="font-semibold text-zinc-800">{p.name || '—'}</span>
                {p.part_no && <span className="font-mono text-zinc-500">{p.part_no}</span>}
                <span className="ml-auto text-zinc-400">
                  {p.register === 'mfg' ? '製造 在庫品目' : '技術資産'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const CompleteDialog = ({ devId, open, onOpenChange, state, isAdmin, onDone, mode = 'complete' }) => {
  const handover = mode === 'handover';
  // 登録先は案件の 種類 でもう決まっている（販売する製品 → 製造品目 など）。
  // 聞き直さず初期選択にし、違うときだけ人が変える。社内工具・治具は選ばせる。
  const [type, setType] = useState(state?.suggested_outcome_type || 'eng_equipment');
  const [form, setForm] = useState({ equipment_name: '', equipment_category: '', location: '',
    installed_on: '', maintenance_notes: '', handover_memo: '', acceptance_name: '',
    acceptance_employee_id: null, product_mode: 'new', product_kind: 'PRD', sku: '', item_name: '',
    uom: '個', qty: 0, item_category: '', notes: '', reason: '', department_id: '',
    equipment_dept_code: 'ENG', equipment_location_id: '', equipment_owner: '',
    equipment_model: '', equipment_inspection_months: '' });
  const [busy, setBusy] = useState(false);
  const [mfgDepts, setMfgDepts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [leftover, setLeftover] = useState({ action: 'keep', part_ids: null, location_id: '' });
  useEffect(() => {
    if (!open) return;
    setLeftover({ action: 'keep', part_ids: null, location_id: '' });
    devApi.mfgDepartments().then((d) => setMfgDepts(d || [])).catch(() => {});
    // P2 · 担当は社員マスターから選ぶ（既定は案件の担当）
    devApi.employees().then((list) => {
      const arr = Array.isArray(list) ? list : (list?.items || []);
      setEmployees(arr.filter((e) => (e.status || 'active') === 'active')
        .map((e) => ({ id: e.id, name: e.name || e.code || e.email || e.id })));
    }).catch(() => setEmployees([]));
    setForm((f) => ({ ...f, equipment_owner: f.equipment_owner
      || (state?.owner_names || [])[0] || '',
      handover_memo: f.handover_memo || state?.handover_memo || '',
      acceptance_name: f.acceptance_name || state?.acceptance_name || '',
      acceptance_employee_id: f.acceptance_employee_id || state?.acceptance_employee_id || null }));
    if (state?.suggested_outcome_type) setType(state.suggested_outcome_type);
  }, [open, state?.suggested_outcome_type, state?.owner_names, state?.handover_memo,
    state?.acceptance_name, state?.acceptance_employee_id]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const patch = (o) => setForm((f) => ({ ...f, ...o }));
  // 2026-11-05 · 品番は必ずルールから。部門＋種類で決まるルールの「次の品番」を見せる。
  const [ruleInfo, setRuleInfo] = useState(null);
  useEffect(() => {
    if (!open || type !== 'mfg_item' || form.product_mode !== 'new' || !form.department_id) {
      setRuleInfo(null);
      return;
    }
    let alive = true;
    devApi.productRule(devId, form.department_id, form.product_kind)
      .then((r) => { if (alive) setRuleInfo(r); })
      .catch((e) => {
        if (alive) setRuleInfo({ ok: false, detail: e?.response?.data?.detail || '品番ルールを確認できませんでした' });
      });
    return () => { alive = false; };
  }, [open, type, devId, form.product_mode, form.department_id, form.product_kind]);
  // 2026-11-04 · ¥0 の注意を読んで「このまま完了」を押すまでは保存させない
  const [zeroCostAck, setZeroCostAck] = useState(false);

  const isEquip = type === 'eng_equipment' || type === 'mfg_equipment';
  const ledger = isEquip || type === 'mfg_item';
  const blocked = [
    ...(state.blocking || []),
    ...((state.unregistered_parts || []).length
      ? [`未登録部品が ${state.unregistered_parts.length} 件あります（先に品目として登録）`] : []),
    ...(ledger && !state.has_prototype_record
      ? ['設備・製品として登録する場合は試作と検証の記録が必要です'] : []),
  ];

  const submit = async (force) => {
    if (leftover.action === 'move' && !leftover.location_id) {
      toast.error('余った部品の移動先の場所を選んでください');
      return;
    }
    if (handover) {
      setBusy(true);
      try {
        const d = await devApi.leftoverParts(devId, {
          action: leftover.action, part_ids: leftover.part_ids,
          location_id: leftover.location_id || null, note: form.notes || null });
        const moved = (d.leftover?.moved || []).length;
        toast.success(leftover.action === 'move'
          ? `引き渡しを登録しました（${moved} 件を移動）`
          : '引き渡しを登録しました（技術に残しました）');
        onOpenChange(false); onDone?.(d);
      } catch (e) {
        toast.error(e.response?.data?.detail || '登録できませんでした');
      }
      setBusy(false);
      return;
    }
    if (type === 'mfg_item' && !form.department_id) {
      toast.error('製品を置く製造部門を選んでください');
      return;
    }
    // P1 · 空欄は null で送る（空文字を数値項目に送ると生の英語エラーになる）
    const num = (v) => (String(v ?? '').trim() === '' ? null : Number(v));
    const txt = (v) => (String(v ?? '').trim() === '' ? null : v);
    if (form.equipment_inspection_months !== ''
      && !Number.isFinite(Number(form.equipment_inspection_months))) {
      toast.error('点検の間隔は数字で入力してください');
      return;
    }
    const payload = {
      ...form,
      equipment_name: txt(form.equipment_name),
      equipment_category: txt(form.equipment_category),
      equipment_location_id: txt(form.equipment_location_id),
      equipment_owner: txt(form.equipment_owner),
      equipment_owner_employee_id: form.equipment_owner_employee_id
        || employees.find((e) => e.name === form.equipment_owner)?.id || null,
      equipment_model: txt(form.equipment_model),
      equipment_inspection_months: num(form.equipment_inspection_months),
      location: txt(form.location),
      installed_on: txt(form.installed_on),
      maintenance_notes: txt(form.maintenance_notes),
      handover_memo: form.handover_memo ?? '',
      acceptance_name: txt(form.acceptance_name),
      acceptance_employee_id: form.acceptance_employee_id
        || employees.find((e) => e.name === form.acceptance_name)?.id || null,
      item_id: txt(form.item_id), item_name: txt(form.item_name), sku: txt(form.sku),
      product_mode: form.product_mode, product_kind: form.product_kind,
      item_category: txt(form.item_category),
      notes: txt(form.notes), reason: txt(form.reason),
      department_id: txt(form.department_id),
    };
    setBusy(true);
    try {
      const d = await devApi.complete(devId, {
        ...payload, final_outcome_type: type, qty: Number(form.qty) || 0, force: !!force,
        leftover_action: leftover.action, leftover_part_ids: leftover.part_ids,
        leftover_location_id: leftover.location_id || null });
      toast.success('完了しました。登録先に反映されています。');
      // 2026-11-05 · 手順書が無いことを知らせる（止めない）
      const reminder = (d?.final_outcome || d?.registration || {}).reminder
        || (d?.outcome || {}).reminder;
      if (reminder) toast.warning(reminder, { duration: 8000 });
      onOpenChange(false); onDone?.(d);
    } catch (e) {
      const det = e.response?.data?.detail;
      if (det?.unmet) {
        toast.error(`${det.message} ${det.unmet.map((u) => u.label).join('、')}`);
      } else {
        toast.error(det || '完了できませんでした');
      }
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-white" data-testid="dev-complete-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{handover ? '引き渡しを登録' : '完了して登録する'}</DialogTitle>
          <DialogDescription className="text-sm">
            {handover
              ? 'この案件はすでに完了しています。ここでは余った部品の行き先だけを決めます。保存するまで何も動きません。'
              : 'この開発が何になったかを決めて登録します。試作・検証・改訂・部品・書類の履歴はすべて残ります。登録は一度だけです。'}
          </DialogDescription>
        </DialogHeader>

        {!handover && blocked.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2"
            data-testid="dev-complete-blockers">
            <p className="text-sm font-bold text-amber-900">完了の条件が未達です</p>
            <ul className="mt-0.5 list-disc pl-5 text-sm text-amber-900">
              {blocked.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          </div>
        )}

        <div className="space-y-3">
          {!handover && state.zero_cost_warning && !zeroCostAck && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2"
              data-testid="dev-complete-zero-cost">
              <p className="text-xs font-bold text-amber-900">最終原価が ¥0 です</p>
              <p className="mt-0.5 text-[11px] text-amber-900">
                {state.zero_cost_warning.message}
                （投入した部品 {state.zero_cost_warning.prototype_lines} 行
                {state.zero_cost_warning.unknown_unit_costs
                  ? ` ・単価未登録 ${state.zero_cost_warning.unknown_unit_costs} 行` : ''}）
              </p>
              <div className="mt-1.5 flex gap-2">
                <Button size="sm" variant="outline" className="h-7 text-[11px]"
                  onClick={() => setZeroCostAck(true)}
                  data-testid="dev-complete-zero-cost-continue">このまま完了</Button>
                <Button size="sm" variant="outline" className="h-7 text-[11px]"
                  onClick={() => onOpenChange(false)}
                  data-testid="dev-complete-zero-cost-back">戻って確認</Button>
              </div>
            </div>
          )}
          {!handover && (state.unregistered_parts || []).length > 0 && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2"
              data-testid="dev-complete-unregistered">
              <p className="text-xs font-bold text-rose-800">
                未登録部品が {state.unregistered_parts.length} 件あります — 先に品目として登録してください
              </p>
              <p className="mt-0.5 text-[11px] text-rose-700">
                {state.unregistered_parts.slice(0, 5).map((u) => u.name || '—').join('、')}
                。部品計画の画面で「新規品目として登録」（品番は品番ルールから発番）してください。
                品番の無い部品は、製品の部品表にも設備の構成にも残せません。
              </p>
            </div>
          )}
          <div className={handover ? 'hidden' : ''}>
            <Label className="text-xs font-semibold text-zinc-700">登録先（最終アウトカム）</Label>
            {state.development_type_label && (
              <p className="text-[11px] text-zinc-500" data-testid="dev-complete-type-hint">
                案件の種類「{state.development_type_label}」から選びました。違うときだけ変えてください。
              </p>
            )}
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="dev-complete-type"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {(state.outcome_types || []).map((o) => (
                  <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!handover && isEquip && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs font-semibold text-zinc-700">設備名</Label>
                <Input value={form.equipment_name} onChange={(e) => set('equipment_name', e.target.value)}
                  placeholder="未入力なら案件名を使います" data-testid="dev-complete-equip-name" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-700">部署（設備番号）</Label>
                <select value={form.equipment_dept_code}
                  onChange={(e) => set('equipment_dept_code', e.target.value)}
                  className="h-10 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm outline-none focus:border-teal-400"
                  data-testid="dev-complete-equip-dept">
                  {[['SNS', 'センスビー製造'], ['SOL', 'ソルダリング製造'], ['HTR', 'ヒーター製造'],
                    ['ENG', '技術'], ['IT', 'IT'], ['CMN', '共通']].map(([c, label]) => (
                      <option key={c} value={c}>{`${label}（${c}/EQP）`}</option>
                    ))}
                </select>
                <p className="mt-1 text-[11px] text-zinc-400">番号はこの部署で付きます（連番は全社で1本）</p>
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-700">種別・カテゴリ</Label>
                <Input value={form.equipment_category} onChange={(e) => set('equipment_category', e.target.value)}
                  placeholder="例: 試験装置" data-testid="dev-complete-equip-category" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-700">設置場所</Label>
                <LocationPicker value={form.equipment_location_id || ''}
                  onChange={(id) => patch({ equipment_location_id: id || '' })}
                  preferDeptCode={form.equipment_dept_code}
                  testId="dev-complete-location" />
                <p className="mt-1 text-[11px] text-zinc-400">
                  選んだ部署（{form.equipment_dept_code}）の場所を先に出します
                </p>
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-700">
                  受入担当（製造側）
                </Label>
                <EmployeePicker value={form.acceptance_name} employees={employees}
                  onChange={(name) => patch({ acceptance_name: name,
                    acceptance_employee_id: employees.find((e) => e.name === name)?.id || null })}
                  testId="dev-complete-acceptance" />
                <p className="mt-1 text-[11px] text-zinc-500">
                  この人が設備の担当者になります。
                </p>
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-700">担当</Label>
                <EmployeePicker value={form.equipment_owner} employees={employees}
                  onChange={(name) => {
                    const row = employees.find((e) => e.name === name);
                    patch({ equipment_owner: name,
                      equipment_owner_employee_id: row?.id || null });
                  }}
                  testId="dev-complete-equip-owner" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-700">型式・版</Label>
                <Input value={form.equipment_model} onChange={(e) => set('equipment_model', e.target.value)}
                  placeholder="例: Rev.A" data-testid="dev-complete-equip-model" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-700">点検の間隔（か月・任意）</Label>
                <Input type="number" min="0" value={form.equipment_inspection_months}
                  onChange={(e) => set('equipment_inspection_months', e.target.value)}
                  placeholder="例: 12" data-testid="dev-complete-equip-inspection" />
                <p className="mt-1 text-[11px] text-zinc-400">
                  メーカーは「自社製作（技術部）」・導入日は引き渡し日で登録します
                </p>
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-700">設置日</Label>
                <Input type="date" value={form.installed_on} onChange={(e) => set('installed_on', e.target.value)}
                  data-testid="dev-complete-installed-on" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs font-semibold text-zinc-700">保守メモ</Label>
                <Textarea rows={2} value={form.maintenance_notes}
                  onChange={(e) => set('maintenance_notes', e.target.value)}
                  data-testid="dev-complete-maintenance" />
              </div>
            </div>
          )}

          {!handover && type === 'mfg_item' && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-semibold text-zinc-700">製造部門（必須）</Label>
                <Select value={form.department_id} onValueChange={(v) => set('department_id', v)}>
                  <SelectTrigger data-testid="dev-complete-department">
                    <SelectValue placeholder="選択してください" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {mfgDepts.map((d) => (
                      <SelectItem key={d.id} value={d.id}
                        data-testid={`dev-complete-department-${d.id}`}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-zinc-600">
                  品目と初期在庫の両方をこの部門に登録します。
                </p>
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-700">登録の方法</Label>
                <Select value={form.product_mode} onValueChange={(v) => set('product_mode', v)}>
                  <SelectTrigger data-testid="dev-complete-product-mode"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="new" data-testid="dev-complete-mode-new">新しい製品として登録</SelectItem>
                    <SelectItem value="improve" data-testid="dev-complete-mode-improve">既存製品の改良（改訂）</SelectItem>
                    <SelectItem value="existing" data-testid="dev-complete-mode-existing">既存品に在庫追加</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.product_mode === 'new' ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Label className="text-xs font-semibold text-zinc-700">種類</Label>
                    <div className="mt-1 flex items-center gap-4 text-sm">
                      {[['PRD', '製品'], ['SFG', '半製品']].map(([v, lb]) => (
                        <label key={v} className="flex items-center gap-1.5">
                          <input type="radio" name="product_kind" value={v}
                            checked={form.product_kind === v}
                            onChange={() => set('product_kind', v)}
                            data-testid={`dev-complete-kind-${v}`} />
                          {lb}
                        </label>
                      ))}
                    </div>
                    {ruleInfo?.ok ? (
                      <p className="mt-2 rounded-md border border-sky-300 bg-sky-50 px-2 py-1.5 text-sm text-sky-900"
                        data-testid="dev-complete-next-sku">
                        次の品番: {ruleInfo.next_sku}（登録時に確定）
                        <span className="ml-1 text-[11px] text-sky-700">{ruleInfo.template_name}</span>
                      </p>
                    ) : ruleInfo ? (
                      <p className="mt-2 rounded-md border border-rose-300 bg-rose-50 px-2 py-1.5 text-sm text-rose-800"
                        data-testid="dev-complete-rule-missing">
                        {ruleInfo.detail}
                        <a href="/settings/sku-master" target="_blank" rel="noopener noreferrer"
                          className="ml-1 font-semibold underline">設定 → 品番と採番</a>
                      </p>
                    ) : null}
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-zinc-700">品名</Label>
                    <Input value={form.item_name} onChange={(e) => set('item_name', e.target.value)}
                      placeholder="未入力なら案件名を使います" data-testid="dev-complete-item-name" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-zinc-700">単位</Label>
                    <Input value={form.uom} onChange={(e) => set('uom', e.target.value)}
                      data-testid="dev-complete-uom" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-zinc-700">初期在庫数（任意）</Label>
                    <Input type="number" min={0} value={form.qty} onChange={(e) => set('qty', e.target.value)}
                      data-testid="dev-complete-qty" />
                  </div>
                </div>
              ) : form.product_mode === 'improve' ? (
                <div className="space-y-3">
                  <p className="text-[11px] text-zinc-600">
                    品番はそのままで、この案件を改訂（rev）として記録します。
                  </p>
                  <ExistingItemPicker onPick={(it) => setForm((f) => ({ ...f, item_id: it.id,
                    item_name: it.name, sku: it.sku }))} picked={form.item_name} />
                  <div>
                    <Label className="text-xs font-semibold text-zinc-700">初期在庫数（任意）</Label>
                    <Input type="number" min={0} value={form.qty}
                      onChange={(e) => set('qty', e.target.value)}
                      data-testid="dev-complete-improve-qty" />
                  </div>
                </div>
              ) : (
                <ExistingItemPicker onPick={(it) => setForm((f) => ({ ...f, item_id: it.id,
                  item_name: it.name, sku: it.sku }))} picked={form.item_name} />
              )}
            </div>
          )}

          <LeftoverPartsSection parts={state.leftover_parts} value={leftover}
            onChange={(patch) => setLeftover((v) => ({ ...v, ...patch }))} />

          <div>
            <Label className="text-xs font-semibold text-zinc-700">
              メモ{type === 'scrapped' ? '' : '（任意）'}
            </Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)}
              data-testid="dev-complete-notes" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-700">
              引継ぎメモ（次の担当へ）
            </Label>
            <p className="text-[11px] text-zinc-500">
              案件に書いたメモを引き継いでいます。ここで仕上げると、この内容が控えに残ります。
            </p>
            <Textarea rows={3} value={form.handover_memo}
              onChange={(e) => set('handover_memo', e.target.value)}
              placeholder="例: 立ち上げ時はヒーターの昇温を10分待つこと。"
              data-testid="dev-complete-handover-memo" />
          </div>
          <div className={handover ? 'hidden' : ''}>
            <Label className="text-xs font-semibold text-zinc-700">
              理由{type === 'scrapped' ? '（必須）' : '（強制完了する場合は必須）'}
            </Label>
            <Textarea rows={2} value={form.reason} onChange={(e) => set('reason', e.target.value)}
              placeholder="例: 検証は合格。運用開始のため設備登録します。"
              data-testid="dev-complete-reason" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>閉じる</Button>
          {handover ? (
            <Button onClick={() => submit(false)} disabled={busy}
              data-testid="dev-handover-submit">
              {busy ? '処理中…' : '引き渡しを保存する'}
            </Button>
          ) : (
            <>
              {blocked.length > 0 && isAdmin && (
                <Button variant="outline" onClick={() => submit(true)} disabled={busy}
                  data-testid="dev-complete-force-btn">
                  <Lock size={14} className="mr-1" /> 条件未達でも完了（管理者）
                </Button>
              )}
              <Button onClick={() => submit(false)}
                disabled={busy || blocked.length > 0
                  || (!!state.zero_cost_warning && !zeroCostAck)}
                data-testid="dev-complete-submit">
                {busy ? '処理中…' : '完了して登録する'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ExistingItemPicker = ({ onPick, picked }) => {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const search = async (v) => {
    setQ(v);
    if (!v.trim()) { setHits([]); return; }
    try {
      const rows = await devApi.masterSearch(v);
      setHits((rows || []).filter((r) => r.source === 'mfg'));
    } catch (_e) { setHits([]); }
  };
  return (
    <div>
      <Label className="text-xs font-semibold text-zinc-700">既存の製造品目</Label>
      <Input value={q} onChange={(e) => search(e.target.value)} placeholder="品名・品番で探す"
        data-testid="dev-complete-item-search" />
      {picked && <p className="mt-1 text-xs font-semibold text-teal-800">選択中: {picked}</p>}
      {hits.length > 0 && (
        <div className="mt-2 max-h-36 divide-y divide-zinc-200 overflow-y-auto rounded-md border border-zinc-300">
          {hits.map((r) => (
            <button key={r.id} type="button" onClick={() => { onPick(r); setQ(''); setHits([]); }}
              data-testid={`dev-complete-item-option-${r.id}`}
              className="block w-full px-3 py-2 text-left text-sm hover:bg-teal-50/60">
              {r.name}{r.sku ? ` · ${r.sku}` : ''}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/** 完了と登録（フェーズ4） — この開発が何になったかを記録し、既存の台帳へ登録する。 */
export const CompletionCard = ({ devId, canEdit, isAdmin, onChanged }) => {
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [handoverOpen, setHandoverOpen] = useState(false);

  const load = useCallback(() => {
    devApi.completion(devId).then(setState).catch(() => {});
  }, [devId]);
  useEffect(() => { load(); }, [load]);

  if (!state) return null;
  const p = state.promotion;
  const done = !!state.completed_at;

  return (
    <section className="space-y-3" data-testid="dev-completion">
      <h2 className="flex items-center gap-1.5 text-lg font-bold text-zinc-900">
        <Gauge size={18} weight="duotone" className="text-zinc-500" /> 完了と登録
      </h2>
      <div className={`rounded-lg border px-4 py-3 shadow-sm ${done
        ? 'border-emerald-300 bg-emerald-50' : 'border-zinc-300 bg-white'}`}>
        {done ? (
          <>
            <p className="flex flex-wrap items-center gap-2 text-sm font-bold text-emerald-900">
              <CheckCircle size={16} weight="fill" /> 完了しました
              <StatusBadge tone="success" testid="dev-completion-outcome">
                {state.final_outcome_label}
              </StatusBadge>
              <span className="text-xs font-normal text-emerald-900">
                {String(state.completed_at).slice(0, 10)}
              </span>
            </p>
            {p && (
              <p className="mt-1 text-sm text-zinc-800" data-testid="dev-completion-promotion">
                登録先: <span className="font-semibold">
                  {[p.no, p.label].filter(Boolean).join(' ')}
                  {p.is_deleted && '（削除済み）'}
                </span>
                {/* 2026-11-05 · 削除された設備は台帳に無いのでリンクを出さない */}
                {!p.is_deleted && (
                  <Button variant="outline" size="sm" className="ml-2 h-7 px-2 text-xs"
                    onClick={() => nav(TARGET_PATH[p.type] || '/engineering')}
                    data-testid="dev-completion-open-target">
                    <ArrowSquareOut size={12} className="mr-1" /> 台帳を開く
                  </Button>
                )}
              </p>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              <Button variant="outline" size="sm"
                onClick={() => window.open(`/engineering/developments/${devId}/handover`, '_blank')}
                data-testid="dev-completion-handover-btn">
                <Printer size={14} className="mr-1" /> 完了報告書・引継ぎ資料
              </Button>
              {canEdit && (state.leftover_parts || []).length > 0 && (
                <Button variant="outline" size="sm" onClick={() => setHandoverOpen(true)}
                  data-testid="dev-completion-register-handover-btn">
                  <Handshake size={14} className="mr-1" /> 引き渡しを登録
                </Button>
              )}
            </div>
            {state.leftover && (
              <p className="mt-1 text-[11px] text-emerald-900" data-testid="dev-completion-leftover">
                余った部品: {state.leftover.action === 'move'
                  ? `納入先へ移動（${(state.leftover.moved || []).length} 件）`
                  : '技術に残す'}
                {state.leftover.at ? ` · ${String(state.leftover.at).slice(0, 10)}` : ''}
              </p>
            )}
          </>
        ) : (
          <>
            <p className="flex items-center gap-1.5 text-sm font-bold text-zinc-900">
              {state.can_complete ? <CheckCircle size={16} className="text-emerald-700" />
                : <WarningCircle size={16} className="text-amber-700" />}
              {state.can_complete ? '完了できます' : '完了の条件を確認してください'}
            </p>
            <ul className="mt-1 space-y-0.5 text-sm" data-testid="dev-completion-gates">
              {(state.gates || []).filter((g) => g.label !== '最終アウトカムが選択されている')
                .map((g, i) => (
                <li key={i} className={g.unmet ? 'text-amber-900' : 'text-zinc-700'}>
                  {g.unmet ? '未達' : '達成'}: {g.label}
                  {g.detail ? `（${g.detail}）` : ''}
                </li>
              ))}
            </ul>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button size="sm" disabled={!canEdit} onClick={() => setOpen(true)}
                data-testid="dev-completion-complete-btn">
                <Gauge size={14} className="mr-1" /> 完了して登録する
              </Button>
              <Button variant="outline" size="sm"
                onClick={() => window.open(`/engineering/developments/${devId}/handover`, '_blank')}
                data-testid="dev-completion-handover-preview-btn">
                <Printer size={14} className="mr-1" /> 引継ぎ資料を見る
              </Button>
            </div>
          </>
        )}
      </div>
      <CompleteDialog devId={devId} open={open} onOpenChange={setOpen} state={state}
        isAdmin={isAdmin} onDone={(d) => { setState(d); onChanged?.(); }} />
      <CompleteDialog devId={devId} open={handoverOpen} onOpenChange={setHandoverOpen}
        state={state} isAdmin={isAdmin} mode="handover"
        onDone={(d) => { setState(d); onChanged?.(); }} />
    </section>
  );
};
